import React, { useState, useEffect } from 'react';
import {
  Container, Box, Grid, Paper, Typography, TextField, Button,
  Card, CardContent, CircularProgress, Collapse, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import {
  TrendingUp, ShoppingCart, AttachMoney, Discount,
  Assessment, ExpandMore, ExpandLess, CalendarMonth
} from '@mui/icons-material';
import { MenuInicial } from "./MenuInicial";
import { API_BASE_URL, VENTASESTADISTICAS } from '../services/apiConfig';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

function VentasAnalisis() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const userData = JSON.parse(sessionStorage.getItem("userData") || '{}');
  const sucursalid = userData?.sucursalid;
  const TOKEN = sessionStorage.getItem("authToken");
  
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [endDate, setEndDate] = useState(new Date());
  const [estadisticas, setEstadisticas] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);
  const [chartsExpanded, setChartsExpanded] = useState({});

  useEffect(() => {
    fetchEstadisticas();
  }, []);

  const fetchEstadisticas = async () => {
    setLoading(true);
    try {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await axios.get(`${API_BASE_URL}ventasestadisticas.php`, {
        params: {
          s: sucursalid,
          startDate: startDateStr,
          endDate: endDateStr,
        },
        headers: TOKEN ? { 'Authorization': `Bearer ${TOKEN}` } : {}
      });
 
if (response.data.success) {
          const data = response.data.data || {};
          const parseNum = (v) => parseFloat(v) || 0;
          
          // Helper para convertir rubro ID a nombre
          const rubroNombres = { 16: 'Bebidas', 17: 'Pizzas', 18: 'Hamburguesas', 19: 'Postres', 20: 'Extras', 21: 'Promos' };
          
          if (Array.isArray(data.ventas_por_metodo_pago)) {
            data.ventas_por_metodo_pago = data.ventas_por_metodo_pago.map(i => ({...i, total: parseNum(i.total), cantidad: parseNum(i.cantidad)}));
          }
          if (Array.isArray(data.ventas_por_tipo)) {
            data.ventas_por_tipo = data.ventas_por_tipo.map(i => ({...i, total: parseNum(i.total), cantidad: parseNum(i.cantidad)}));
          }
          if (Array.isArray(data.ventas_por_rubro)) {
            data.ventas_por_rubro = data.ventas_por_rubro.map(i => ({
              ...i,
              total: parseNum(i.total),
              cantidad: parseNum(i.cantidad),
              categoria: 'Rubro ' + i.producto_rubro_id
            }));
          }
          if (Array.isArray(data.ventas_por_dia)) {
            data.ventas_por_dia = data.ventas_por_dia.map(i => ({...i, total_ventas: parseNum(i.total_ventas), cantidad_ventas: parseNum(i.cantidad_ventas)}));
          }
          if (Array.isArray(data.ventas_por_hora)) {
            data.ventas_por_hora = data.ventas_por_hora.map(i => ({...i, total_ventas: parseNum(i.total_ventas), cantidad_ventas: parseNum(i.cantidad_ventas)}));
          }
          if (data.resumen) {
            data.resumen = {
              ...data.resumen,
              monto_total: parseNum(data.resumen.monto_total),
              ticket_promedio: parseNum(data.resumen.ticket_promedio),
              descuentos_totales: parseNum(data.resumen.descuentos_totales),
              total_ventas: parseNum(data.resumen.total_ventas)
            };
          }
          setEstadisticas(data);
        } else {
          console.error('Error:', response.data?.error);
        }
       setLoading(false);
     } catch (error) {
       console.error('Error al cargar estadísticas', error);
       setLoading(false);
     }
   };

  const handleBuscar = () => {
    fetchEstadisticas();
  };
 
   const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-AR').format(value || 0);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, compact = false }) => (
    <Card sx={{ height: '100%', minHeight: compact ? 80 : 'auto' }}>
      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: compact ? 'center' : 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              color="text.secondary" 
              variant="caption" 
              sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem', display: 'block' }}
            >
              {title}
            </Typography>
            <Typography 
              variant={compact ? "h6" : (isMobile ? "h5" : "h4")} 
              sx={{ 
                color: color, 
                fontWeight: 'bold',
                fontSize: isMobile ? '1.1rem !important' : 'inherit',
                wordBreak: 'break-word'
              }}
            >
              {value}
            </Typography>
            {subtitle && !compact && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: 1.5,
              p: compact ? 0.75 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Icon sx={{ color: color, fontSize: compact ? 20 : (isMobile ? 24 : 32) }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, children, defaultExpanded = true }) => {
    const isExpanded = chartsExpanded[title] ?? defaultExpanded;
    return (
      <Paper sx={{ p: isMobile ? 1.5 : 3, mb: isMobile ? 2 : 0 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: isExpanded ? 2 : 0,
            cursor: 'pointer'
          }}
          onClick={() => setChartsExpanded(prev => ({ ...prev, [title]: !prev[title] }))}
        >
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            sx={{ fontWeight: 600 }}
          >
            {title}
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={isExpanded}>
          {children}
        </Collapse>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <MenuInicial />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <MenuInicial />

      <Box sx={{ my: isMobile ? 1 : 3, px: isMobile ? 0 : 0 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center',
          mb: 2,
          gap: 1
        }}>
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              gutterBottom 
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Assessment fontSize={isMobile ? "small" : "inherit"} /> 
              {isMobile ? 'Análisis' : 'Análisis de Ventas'}
            </Typography>
            {!isMobile && (
              <Typography variant="body2" color="text.secondary">
                Dashboard de métricas y estadísticas
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => setFiltersOpen(!filtersOpen)}
              sx={{ display: isMobile ? 'flex' : 'none', mr: 1 }}
            >
              <CalendarMonth />
            </IconButton>
            <Collapse in={!isMobile || filtersOpen} orientation={isMobile ? "vertical" : "horizontal"}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', 
                gap: isMobile ? 1 : 2, 
                alignItems: 'center' 
              }}>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  customInput={<TextField label="Desde" size="small" sx={{ width: isMobile ? '100%' : 130 }} />}
                />
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  dateFormat="dd/MM/yyyy"
                  customInput={<TextField label="Hasta" size="small" sx={{ width: isMobile ? '100%' : 130 }} />}
                />
                <Button variant="contained" onClick={handleBuscar} size="small">
                  Buscar
                </Button>
              </Box>
            </Collapse>
          </Box>
        </Box>

        {estadisticas && (
          <>
            <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 2 : 3 }}>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Ventas"
                  value={formatNumber(estadisticas.resumen?.total_ventas)}
                  subtitle="transacciones"
                  icon={ShoppingCart}
                  color="#1976d2"
                  compact={isMobile}
                />
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'center' }}>
                  {estadisticas.ventas_por_tipo?.map(t => (
                    <Box key={t.tipo_venta} sx={{ fontSize: 10, color: { delivery: '#e91e63', mesa: '#2196f3', mostrador: '#4caf50' }[t.tipo_venta] }}>
                      {t.tipo_venta}: {t.cantidad}
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Monto"
                  value={formatCurrency(estadisticas.resumen?.monto_total)}
                  subtitle="ingresos"
                  icon={AttachMoney}
                  color="#2e7d32"
                  compact={isMobile}
                />
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'center' }}>
                  {estadisticas.ventas_por_tipo?.map(t => (
                    <Box key={t.tipo_venta} sx={{ fontSize: 10, color: { delivery: '#e91e63', mesa: '#2196f3', mostrador: '#4caf50' }[t.tipo_venta] }}>
                      {t.tipo_venta}: {formatCurrency(t.total)}
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Ticket"
                  value={formatCurrency(estadisticas.resumen?.ticket_promedio)}
                  subtitle="promedio"
                  icon={TrendingUp}
                  color="#ed6c02"
                  compact={isMobile}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Descuentos"
                  value={formatCurrency(estadisticas.resumen?.descuentos_totales)}
                  subtitle="totales"
                  icon={Discount}
                  color="#d32f2f"
                  compact={isMobile}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
              <ChartCard title="Ventas Diarias">
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                  <LineChart data={estadisticas.ventas_por_dia}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="fecha" 
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'total_ventas') return [formatCurrency(value), 'Monto'];
                        return [value, name];
                      }}
                      contentStyle={{ fontSize: isMobile ? 11 : 13 }}
                    />
                    {!isMobile && <Legend />}
                    <Line 
                      type="monotone" 
                      dataKey="total_ventas" 
                      stroke="#1976d2" 
                      strokeWidth={2}
                      dot={{ r: isMobile ? 2 : 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={6}>
                  <ChartCard title="Métodos de Pago">
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                      <PieChart>
                        <Pie
                          data={estadisticas.ventas_por_metodo_pago}
                          dataKey="total"
                          nameKey="metodo_pago"
                          cx="50%"
                          cy="50%"
                          outerRadius={isMobile ? 60 : 80}
                          label={!isMobile}
                          labelLine={!isMobile}
                        >
                          {estadisticas.ventas_por_metodo_pago?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ fontSize: 12 }} />
                        {!isMobile && <Legend />}
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ChartCard title="Productos Más Vendidos">
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                      <BarChart 
                        data={estadisticas.productos_mas_vendidos?.slice(0, 5)}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: isMobile ? 9 : 11 }} />
                        <YAxis 
                          type="category" 
                          dataKey="nombre" 
                          width={isMobile ? 80 : 120}
                          tick={{ fontSize: isMobile ? 9 : 11 }}
                        />
                        <Tooltip formatter={(value) => [value, 'Cantidad']} contentStyle={{ fontSize: 11 }} />
                        <Bar dataKey="cantidad_vendida" fill="#1976d2" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ChartCard title="Por Categoría">
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                      <BarChart data={estadisticas.ventas_por_rubro}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="categoria" tick={{ fontSize: isMobile ? 9 : 11 }} />
                        <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} />
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ fontSize: 11 }} />
                        <Bar dataKey="total" fill="#2e7d32" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ChartCard title="Por Hora">
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                      <BarChart data={estadisticas.ventas_por_hora}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="hora" 
                          tickFormatter={(value) => `${value}h`}
                          tick={{ fontSize: isMobile ? 9 : 11 }}
                        />
                        <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} />
                        <Tooltip 
                          labelFormatter={(value) => `Hora: ${value}:00`}
                          formatter={(value) => [value, 'Ventas']}
                          contentStyle={{ fontSize: 11 }}
                        />
                        <Bar dataKey="cantidad_ventas" fill="#ed6c02" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ChartCard title="Por Tipo (Delivery/Mesa/Mostrador)">
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                      <PieChart>
                        <Pie
                          data={estadisticas.ventas_por_tipo}
                          dataKey="total"
                          nameKey="tipo_venta"
                          cx="50%"
                          cy="50%"
                          outerRadius={isMobile ? 60 : 80}
                          label={({ name, percent }) => `${name || 'Otro'} ${(percent * 100).toFixed(0)}%`}
                        >
                          {estadisticas.ventas_por_tipo?.map((entry, index) => {
                            const colors = { 'delivery': '#e91e63', 'mesa': '#2196f3', 'mostrador': '#4caf50' };
                            return <Cell key={`cell-${index}`} fill={colors[entry.tipo_venta] || COLORS[index % COLORS.length]} />;
                          })}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ fontSize: 12 }} />
                        {!isMobile && <Legend />}
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
}

export default VentasAnalisis;
